// ============================================
// PERFORMANCE UTILITIES
// ============================================
const Utils = {
  // Debounce function for performance
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  },

  // Throttle using requestAnimationFrame
  rafThrottle(func) {
    let rafId = null;
    return function(...args) {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        func.apply(this, args);
        rafId = null;
      });
    };
  },

  // Pad numbers
  pad: num => (num < 10 ? `0${num}` : num),

  // Check if mobile (cached)
  _isMobileCache: null,
  isMobile() {
    if (this._isMobileCache === null) {
      this._isMobileCache = window.innerWidth < 750;
    }
    return this._isMobileCache;
  },
  resetMobileCache() {
    this._isMobileCache = null;
  },

  // Initialize on DOM ready or Shopify section load
  initOnReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
    document.addEventListener('shopify:section:load', callback);
  }
};

// ============================================
// GLOBAL RESIZE MANAGER (Single listener)
// ============================================
const ResizeManager = (() => {
  const callbacks = new Set();
  let resizeRAF = null;

  const handleResize = () => {
    if (resizeRAF) return;
    resizeRAF = requestAnimationFrame(() => {
      Utils.resetMobileCache();
      callbacks.forEach(cb => cb());
      resizeRAF = null;
    });
  };

  window.addEventListener('resize', handleResize, { passive: true });

  return {
    subscribe: cb => callbacks.add(cb),
    unsubscribe: cb => callbacks.delete(cb)
  };
})();

// ============================================
// CLEANUP MANAGER (Memory leak prevention)
// ============================================
const CleanupManager = (() => {
  const cleanupFunctions = new Map();

  document.addEventListener('shopify:section:unload', e => {
    const sectionId = e.detail?.sectionId || e.target?.id;
    if (sectionId && cleanupFunctions.has(sectionId)) {
      cleanupFunctions.get(sectionId).forEach(fn => fn());
      cleanupFunctions.delete(sectionId);
    }
  });

  return {
    register: (sectionId, cleanupFn) => {
      if (!cleanupFunctions.has(sectionId)) {
        cleanupFunctions.set(sectionId, []);
      }
      cleanupFunctions.get(sectionId).push(cleanupFn);
    }
  };
})();

// ============================================
// STICKY HEADER (Optimized)
// ============================================
(() => {
  const header = document.querySelector("sticky-header");
  if (!header) return;

  const CONFIG = {
    SCROLL_THRESHOLD: 200,
    HYSTERESIS_BUFFER: 150,
    MIN_STATE_DURATION: 200,
    SCROLL_STOP_DELAY: 400
  };

  let state = {
    isSticky: false,
    lastStateChangeTime: 0,
    scrollStopTimer: null,
    lastScrollTop: 0
  };

  const showCompactHeader = () => {
    const scrollTop = window.pageYOffset;
    if (!state.isSticky && scrollTop >= CONFIG.SCROLL_THRESHOLD) {
      header.classList.add("show-sticky");
      state.isSticky = true;
      state.lastStateChangeTime = Date.now();
    }
  };

  const updateHeader = () => {
    const scrollTop = window.pageYOffset;
    
    if (Math.abs(scrollTop - state.lastScrollTop) < 5) return;
    state.lastScrollTop = scrollTop;

    const now = Date.now();

    clearTimeout(state.scrollStopTimer);
    state.scrollStopTimer = setTimeout(showCompactHeader, CONFIG.SCROLL_STOP_DELAY);

    if (now - state.lastStateChangeTime < CONFIG.MIN_STATE_DURATION) return;

    const thresholdAdd = CONFIG.SCROLL_THRESHOLD;
    const thresholdRemove = CONFIG.SCROLL_THRESHOLD - CONFIG.HYSTERESIS_BUFFER;

    if (!state.isSticky && scrollTop >= thresholdAdd) {
      header.classList.add("show-sticky");
      state.isSticky = true;
      state.lastStateChangeTime = now;
    } else if (state.isSticky && scrollTop <= thresholdRemove) {
      header.classList.remove("show-sticky");
      state.isSticky = false;
      state.lastStateChangeTime = now;
    }
  };

  const init = () => {
    const scrollTop = window.pageYOffset;
    state.lastScrollTop = scrollTop;
    state.lastStateChangeTime = Date.now();
    
    header.classList.toggle("show-sticky", scrollTop >= CONFIG.SCROLL_THRESHOLD);
    state.isSticky = scrollTop >= CONFIG.SCROLL_THRESHOLD;

    window.addEventListener("scroll", Utils.rafThrottle(updateHeader), { passive: true });
  };

  // run immediately (script is deferred, DOM is ready): a delayed class flip on
  // restored-scroll loads lands after first paint and reads as header CLS
  init();
})();

// ============================================
// MEGA MENU EDGE ALIGNMENT (LEFT/RIGHT ONLY)
// ============================================
(() => {
  const MEGA_MENU_SELECTOR = 'details.mega-menu';
  const CONTENT_SELECTOR = '.mega-menu__content';

  const applyAlignment = details => {
    const content = details.querySelector(CONTENT_SELECTOR);
    if (!content) return;

    content.classList.remove('align-left', 'align-right');

    const rect = content.getBoundingClientRect();
    const vw = window.innerWidth;

    if (rect.right > vw) {
      content.classList.add('align-right');
    } else if (rect.left < 0) {
      content.classList.add('align-left');
    }
  };

  const adjustMenu = Utils.rafThrottle(details => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyAlignment(details);
      });
    });
  });

  const initMegaMenus = () => {
    document
      .querySelectorAll(`${MEGA_MENU_SELECTOR}:not([data-align-init])`)
      .forEach(details => {
        details.dataset.alignInit = 'true';

        const onToggle = () => {
          if (!details.open) return;
          adjustMenu(details);
        };

        details.addEventListener('toggle', onToggle);

        const resizeHandler = () => {
          if (details.open) adjustMenu(details);
        };

        ResizeManager.subscribe(resizeHandler);

        CleanupManager.register(
          details.closest('[id]')?.id || 'mega-menu',
          () => {
            ResizeManager.unsubscribe(resizeHandler);
            details.removeEventListener('toggle', onToggle);
          }
        );
      });
  };

  Utils.initOnReady(initMegaMenus);
})();

// ============================================
// SALE TIMER (Optimized - Single RAF loop)
// ============================================
(() => {
  const timers = [];
  let rafId = null;

  const updateAllTimers = () => {
    const now = Date.now();
    let anyActive = false;

    timers.forEach(timer => {
      const { start, end, timerLabel, timerBox, originalLabel, elements } = timer;

      if (now < start) {
        timerLabel.textContent = "SALE STARTS SOON";
        timerBox.classList.add("hidden");
        return;
      }

      if (now >= end) {
        timerLabel.textContent = "SALE ENDED";
        timerBox.classList.add("hidden");
        return;
      }

      anyActive = true;
      timerLabel.textContent = originalLabel;
      timerBox.classList.remove("hidden");

      const diff = end - now;
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      if (elements.days && timer.lastD !== d) {
        elements.days.textContent = Utils.pad(d);
        timer.lastD = d;
      }
      if (elements.hours && timer.lastH !== h) {
        elements.hours.textContent = Utils.pad(h);
        timer.lastH = h;
      }
      if (elements.minutes && timer.lastM !== m) {
        elements.minutes.textContent = Utils.pad(m);
        timer.lastM = m;
      }
      if (elements.seconds && timer.lastS !== s) {
        elements.seconds.textContent = Utils.pad(s);
        timer.lastS = s;
      }
    });

    if (anyActive) {
      rafId = requestAnimationFrame(updateAllTimers);
    } else {
      rafId = null;
    }
  };

  const initTimers = () => {
    document.querySelectorAll('[id^="timer-"]').forEach(timerBox => {
      if (timerBox.dataset.initialized === 'true') return;
      
      const { startDate, endDate, originalLabel } = timerBox.dataset;
      if (!startDate || !endDate || !originalLabel) return;

      const start = new Date(startDate).getTime();
      const end = new Date(endDate).getTime();
      const timerLabel = timerBox.previousElementSibling;

      if (isNaN(start) || isNaN(end) || !timerLabel) return;

      timerBox.dataset.initialized = 'true';

      timers.push({
        start,
        end,
        timerLabel,
        timerBox,
        originalLabel,
        elements: {
          days: timerBox.querySelector('[data-unit="days"] .value'),
          hours: timerBox.querySelector('[data-unit="hours"] .value'),
          minutes: timerBox.querySelector('[data-unit="minutes"] .value'),
          seconds: timerBox.querySelector('[data-unit="seconds"] .value')
        },
        lastD: null,
        lastH: null,
        lastM: null,
        lastS: null
      });
    });

    if (timers.length > 0 && !rafId) {
      rafId = requestAnimationFrame(updateAllTimers);
    }
  };

  Utils.initOnReady(initTimers);
})();

// ============================================
// FOOTER TOGGLE (Optimized)
// ============================================
Utils.initOnReady(() => {
  const toggles = Array.from(document.querySelectorAll('.footer-toggle'));
  if (toggles.length === 0) return;

  const heights = new Map();

  toggles.forEach(toggle => {
    const list = toggle.nextElementSibling;
    if (list) {
      heights.set(toggle, list.scrollHeight);
    }

    toggle.addEventListener('click', function() {
      const currentList = this.nextElementSibling;
      if (!currentList) return;

      const isOpen = currentList.style.maxHeight && currentList.style.maxHeight !== "0px";

      requestAnimationFrame(() => {
        toggles.forEach(other => {
          if (other !== toggle) {
            other.nextElementSibling.style.maxHeight = "0px";
            other.classList.remove("active");
          }
        });

        currentList.style.maxHeight = isOpen ? "0px" : `${heights.get(toggle)}px`;
        this.classList.toggle("active", !isOpen);
      });
    });
  });
});

// ============================================
// ACCORDION (Optimized)
// ============================================
(function() {
  'use strict';

  if (window.accordionInitialized) return;
  window.accordionInitialized = true;

  const ACCORDION_SELECTOR = 'details.accordion-item';
  const initializedAccordions = new WeakSet();

  function initSingleAccordion(item) {
    if (initializedAccordions.has(item)) return;
    initializedAccordions.add(item);

    if (item._accordionToggleHandler) {
      item.removeEventListener('toggle', item._accordionToggleHandler);
    }

    const toggleHandler = function() {
      if (this.open) {
        requestAnimationFrame(() => {
          this.classList.add('active');

          const sectionClass = Array.from(this.classList).find(cls => 
            cls.startsWith('section-')
          );

          const selector = sectionClass 
            ? `${ACCORDION_SELECTOR}.${sectionClass}`
            : ACCORDION_SELECTOR;

          document.querySelectorAll(selector).forEach(other => {
            if (other !== this && other.open) {
              other.open = false;
              other.classList.remove('active');
            }
          });
        });
      } else {
        this.classList.remove('active');
      }
    };

    item._accordionToggleHandler = toggleHandler;
    item.addEventListener('toggle', toggleHandler);
  }

  function initAllAccordions() {
    const accordions = document.querySelectorAll(ACCORDION_SELECTOR);
    if (accordions.length > 0) {
      accordions.forEach(initSingleAccordion);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllAccordions, { once: true });
  } else {
    initAllAccordions();
  }

  document.addEventListener('shopify:section:load', () => {
    setTimeout(initAllAccordions, 50);
  });

  if (window.MutationObserver) {
    let debounceTimer = null;
    
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const newAccordions = document.querySelectorAll(`${ACCORDION_SELECTOR}:not([data-observed])`);
        if (newAccordions.length > 0) {
          newAccordions.forEach(acc => {
            acc.dataset.observed = 'true';
            initSingleAccordion(acc);
          });
        }
      }, 100);
    });

    setTimeout(() => {
      const mainContent = document.querySelector('main') || document.body;
      observer.observe(mainContent, {
        childList: true,
        subtree: true
      });
    }, 500);
  }
})();

// ============================================
// ROTATING BOLD ITEMS (Optimized)
// ============================================
Utils.initOnReady(() => {
  if (Utils.isMobile()) return;

  const items = document.querySelectorAll('.content-wrapper.original .item');
  if (items.length === 0) return;

  let index = 0;
  
  const rotate = () => {
    requestAnimationFrame(() => {
      items.forEach((el, i) => el.classList.toggle('active', i === index));
    });
    index = (index + 1) % items.length;
  };

  rotate();
  setInterval(rotate, 1500);
});

// ============================================
// SEO READ MORE (Optimized)
// ============================================
Utils.initOnReady(() => {
  const wrapper = document.querySelector('.seo-content-wrapper');
  const btn = document.querySelector('.seo-readmore-btn');
  if (!wrapper || !btn) return;

  const blocks = Array.from(wrapper.querySelectorAll('p, h1, h2, h3, h4, h5, h6'));
  const hiddenBlocks = blocks.slice(6);
  
  requestAnimationFrame(() => {
    hiddenBlocks.forEach(el => el.classList.add('hidden-block'));
  });

  const btnText = btn.querySelector('span');
  
  btn.addEventListener('click', () => {
    const isExpanded = wrapper.classList.toggle('expanded');
    if (btnText) btnText.textContent = isExpanded ? 'Read less' : 'Read more';
  });
});

// ============================================
// MAIN BESTSELLER WRAPPER SLIDER
// ============================================
(() => {
  class MainBestsellerSlider {
    constructor(container) {
      this.container = container;
      this.wrapperContainer = container.querySelector('.bestseller-wrapper-container');
      
      if (!this.wrapperContainer) return;
      
      this.wrappers = Array.from(this.wrapperContainer.querySelectorAll('.bestseller-wrapper'));
      this.controls = container.querySelector('.bestseller-main-controls');
      
      if (this.wrappers.length <= 1) {
        if (this.controls) {
          this.controls.style.display = 'none';
        }
        if (this.wrappers.length === 1) {
          this.wrappers[0].classList.add('active');
        }
        return;
      }

      if (!this.controls) return;

      this.dots = Array.from(this.controls.querySelectorAll('.slider-dot'));
      this.prevBtn = this.controls.querySelector('.slider-nav.prev');
      this.nextBtn = this.controls.querySelector('.slider-nav.next');
      
      this.currentIndex = 0;
      this.totalSlides = this.wrappers.length;
      this.autoplayInterval = null;
      this.autoplayDelay = parseInt(container.dataset.autoplayInterval) || 5000;
      this.isAnimating = false;
      
      this.init();
    }

    init() {
      this.container.dataset.mainSliderInitialized = 'true';
      this.setupEventListeners();
      this.updateSlider(0, null, false);
      this.startAutoplay();
    }

    setupEventListeners() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.isAnimating) return;
          this.goToPrevSlide();
          this.restartAutoplay();
        });
      }
      
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.isAnimating) return;
          this.goToNextSlide();
          this.restartAutoplay();
        });
      }
      
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.isAnimating) return;
          const targetIndex = parseInt(dot.dataset.index);
          this.goToSlide(targetIndex);
          this.restartAutoplay();
        });
      });

      this.container.addEventListener('mouseenter', () => {
        this.pauseAutoplay();
      }, { passive: true });
      
      this.container.addEventListener('mouseleave', () => {
        this.startAutoplay();
      }, { passive: true });

      const keyHandler = (e) => {
        if (!this.isInViewport() || this.isAnimating) return;
        
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goToPrevSlide();
          this.restartAutoplay();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goToNextSlide();
          this.restartAutoplay();
        }
      };
      
      document.addEventListener('keydown', keyHandler);
      this.keyHandler = keyHandler;

      let touchStartX = 0;
      let touchEndX = 0;

      this.container.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        this.pauseAutoplay();
      }, { passive: true });

      this.container.addEventListener('touchend', (e) => {
        if (this.isAnimating) return;
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
        this.startAutoplay();
      }, { passive: true });
    }

    handleSwipe(startX, endX) {
      const diff = startX - endX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.goToNextSlide();
        } else {
          this.goToPrevSlide();
        }
        this.restartAutoplay();
      }
    }

    isInViewport() {
      const rect = this.container.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }

    updateSlider(newIndex, direction = null, animate = true) {
      if (!animate) {
        this.wrappers.forEach((wrapper, i) => {
          wrapper.classList.toggle('active', i === newIndex);
        });
        this.updateDots(newIndex);
        this.currentIndex = newIndex;
        return;
      }

      this.isAnimating = true;
      const oldIndex = this.currentIndex;
      const oldWrapper = this.wrappers[oldIndex];
      const newWrapper = this.wrappers[newIndex];

      requestAnimationFrame(() => {
        if (direction === null) {
          direction = newIndex > oldIndex ? 'next' : 'prev';
        }

        this.wrappers.forEach(w => {
          w.classList.remove('slide-out-left', 'slide-out-right', 'slide-in-left', 'slide-in-right');
        });

        if (direction === 'next') {
          newWrapper.classList.add('slide-in-right');
        } else {
          newWrapper.classList.add('slide-in-left');
        }

        newWrapper.offsetHeight;

        if (direction === 'next') {
          oldWrapper.classList.add('slide-out-left');
          oldWrapper.classList.remove('active');
        } else {
          oldWrapper.classList.add('slide-out-right');
          oldWrapper.classList.remove('active');
        }

        newWrapper.classList.add('active');
        newWrapper.classList.remove('slide-in-left', 'slide-in-right');

        this.updateDots(newIndex);

        setTimeout(() => {
          oldWrapper.classList.remove('slide-out-left', 'slide-out-right');
          this.isAnimating = false;
        }, 600);
      });

      this.currentIndex = newIndex;
    }

    updateDots(activeIndex) {
      this.dots.forEach((dot, i) => {
        const isActive = i === activeIndex;
        dot.classList.toggle('active', isActive);
        
        const svgs = dot.querySelectorAll('svg.icon');
        svgs.forEach(svg => {
          const isActiveSvg = svg.classList.contains('active');
          if (isActive) {
            svg.style.display = isActiveSvg ? 'block' : 'none';
          } else {
            svg.style.display = isActiveSvg ? 'none' : 'block';
          }
        });
      });
      
      // Update mobile title wrappers
      const mobileTitles = this.container.querySelectorAll('.title-wrapper.large-up-hide');
      mobileTitles.forEach((title, i) => {
        title.classList.toggle('active', i === activeIndex);
      });
    }

    goToSlide(index, direction = null) {
      if (index < 0 || index >= this.totalSlides || index === this.currentIndex) return;
      if (this.isAnimating) return;
      
      if (direction === null) {
        direction = index > this.currentIndex ? 'next' : 'prev';
      }
      
      this.updateSlider(index, direction);
    }

    goToNextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      this.updateSlider(nextIndex, 'next');
    }

    goToPrevSlide() {
      const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      this.updateSlider(prevIndex, 'prev');
    }

    startAutoplay() {
      if (this.totalSlides <= 1) return;
      
      this.pauseAutoplay();
      
      this.autoplayInterval = setInterval(() => {
        if (!this.isAnimating) {
          this.goToNextSlide();
        }
      }, this.autoplayDelay);
    }

    pauseAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    restartAutoplay() {
      this.pauseAutoplay();
      this.startAutoplay();
    }
    
    destroy() {
      this.pauseAutoplay();
      if (this.keyHandler) {
        document.removeEventListener('keydown', this.keyHandler);
      }
    }
  }

  const initMainBestsellerSliders = () => {
    const sections = document.querySelectorAll('.bestseller, [data-autoplay-interval]');
    
    sections.forEach((section, index) => {
      if (section.dataset.mainSliderInitialized === 'true') return;
      new MainBestsellerSlider(section);
    });
  };

  if (window.Utils && window.Utils.initOnReady) {
    Utils.initOnReady(initMainBestsellerSliders);
  } else {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initMainBestsellerSliders, { once: true });
    } else {
      initMainBestsellerSliders();
    }
    document.addEventListener('shopify:section:load', initMainBestsellerSliders);
  }
})();

// ============================================
// PRODUCT IMAGE SLIDER (Individual Products)
// ============================================
(() => {
  class ProductImageSlider {
    constructor(container) {
      this.container = container;
      this.sliderId = container.dataset.sliderId;
      this.track = container.querySelector('.slider-track');
      this.slides = Array.from(container.querySelectorAll('.slider__slide'));
      
      if (!this.track || this.slides.length === 0) return;
      
      this.controls = document.querySelector(`[data-controls-id="${this.sliderId}"]`);
      
      if (!this.controls) return;
      
      this.dots = Array.from(this.controls.querySelectorAll('.slider-dot'));
      this.prevBtn = this.controls.querySelector('.slider-nav.prev');
      this.nextBtn = this.controls.querySelector('.slider-nav.next');
      
      if (this.slides.length <= 1) {
        this.controls.style.display = 'none';
        return;
      }
      
      this.currentIndex = 0;
      this.totalSlides = this.slides.length;
      
      this.init();
    }
    
    init() {
      this.container.dataset.productSliderInitialized = 'true';
      this.setupEventListeners();
      this.updateSlider();
    }
    
    setupEventListeners() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToPrevSlide();
        });
      }
      
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToNextSlide();
        });
      }
      
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const targetIndex = parseInt(dot.dataset.index);
          this.goToSlide(targetIndex);
        });
      });
      
      let touchStartX = 0;
      let touchEndX = 0;
      
      this.container.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      
      this.container.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });
    }
    
    handleSwipe(startX, endX) {
      const diff = startX - endX;
      const threshold = 50;
      
      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.goToNextSlide();
        } else {
          this.goToPrevSlide();
        }
      }
    }
    
    updateSlider() {
      requestAnimationFrame(() => {
        const slideWidth = this.slides[0].offsetWidth;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const offset = -(this.currentIndex * (slideWidth + gap));
        
        this.track.style.transform = `translateX(${offset}px)`;
        
        this.dots.forEach((dot, i) => {
          const isActive = i === this.currentIndex;
          dot.classList.toggle('active', isActive);
          
          const iconSvgs = dot.querySelectorAll('svg.icon');
          if (iconSvgs.length > 0) {
            iconSvgs.forEach(svg => {
              const isActiveSvg = svg.classList.contains('active');
              if (isActive) {
                svg.style.display = isActiveSvg ? 'block' : 'none';
              } else {
                svg.style.display = isActiveSvg ? 'none' : 'block';
              }
            });
          } else {
            const svgInactive = dot.querySelector('svg:not(.active)');
            const svgActive = dot.querySelector('svg.active');
            
            if (svgInactive && svgActive) {
              svgInactive.style.display = isActive ? 'none' : 'block';
              svgActive.style.display = isActive ? 'block' : 'none';
            }
          }
        });
        
        if (this.prevBtn) {
          this.prevBtn.disabled = false;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = false;
        }
      });
    }
    
    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides) return;
      this.currentIndex = index;
      this.updateSlider();
    }
    
    goToNextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
      this.updateSlider();
    }
    
    goToPrevSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      this.updateSlider();
    }
  }
  
  const initProductImageSliders = () => {
    const sliders = document.querySelectorAll('.product__media-slider:not([data-product-slider-initialized])');
    sliders.forEach((slider, index) => {
      new ProductImageSlider(slider);
    });
  };
  
  if (window.Utils && window.Utils.initOnReady) {
    Utils.initOnReady(initProductImageSliders);
  } else {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initProductImageSliders, { once: true });
    } else {
      initProductImageSliders();
    }
    document.addEventListener('shopify:section:load', initProductImageSliders);
  }
})();

// ============================================
// SHOP BY ROOM
// ============================================
(() => {
  window.switchRoom = (button, sectionId) => {
    const container = document.getElementById(`shop-by-room-${sectionId}`);
    if (!container) return;
    
    const roomId = button.dataset.room;
    
    if (button.classList.contains('active')) return;
    
    requestAnimationFrame(() => {
      container.querySelectorAll('.shop-by-room__tab').forEach(tab => {
        tab.classList.remove('active');
      });
      button.classList.add('active');
      
      container.querySelectorAll('.shop-by-room__room').forEach(room => {
        room.classList.remove('active');
      });
      const activeRoom = container.querySelector(`#room-${roomId}`);
      if (activeRoom) activeRoom.classList.add('active');
      
      container.querySelectorAll('.shop-by-room__product-slider').forEach(slider => {
        slider.classList.remove('active');
        slider.querySelectorAll('.shop-by-room__product-card').forEach(card => {
          card.classList.remove('active');
        });
      });
      
      const firstSlider = container.querySelector(`.shop-by-room__product-slider[data-room="${roomId}"][data-image="0"]`);
      if (firstSlider) {
        firstSlider.classList.add('active');
        const firstProduct = firstSlider.querySelector('.shop-by-room__product-card[data-product-index="0"]');
        if (firstProduct) firstProduct.classList.add('active');
      }
      
      if (activeRoom) {
        activeRoom.querySelectorAll('.shop-by-room__thumbnail').forEach((thumb, i) => {
          thumb.classList.toggle('active', i === 0);
        });
        
        activeRoom.querySelectorAll('.shop-by-room__image').forEach((img, i) => {
          img.style.display = i === 0 ? 'block' : 'none';
        });
        
        activeRoom.querySelectorAll('.shop-by-room__hotspots-wrapper').forEach((wrapper, i) => {
          wrapper.classList.toggle('active', i === 0);
        });
      }
    });
  };

  window.switchImage = (thumbnail, roomId, imageIndex, sectionId) => {
    const room = document.getElementById(`room-${roomId}`);
    const container = document.getElementById(`shop-by-room-${sectionId}`);
    if (!room || !container) return;
    
    requestAnimationFrame(() => {
      room.querySelectorAll('.shop-by-room__thumbnail').forEach(thumb => {
        thumb.classList.remove('active');
      });
      thumbnail.classList.add('active');
      
      room.querySelectorAll('.shop-by-room__image').forEach(img => {
        img.style.display = 'none';
      });
      const targetImage = room.querySelector(`.shop-by-room__image[data-image-index="${imageIndex}"]`);
      if (targetImage) targetImage.style.display = 'block';
      
      room.querySelectorAll('.shop-by-room__hotspots-wrapper').forEach(wrapper => {
        wrapper.classList.remove('active');
      });
      const targetHotspot = room.querySelector(`.shop-by-room__hotspots-wrapper[data-image-index="${imageIndex}"]`);
      if (targetHotspot) targetHotspot.classList.add('active');
      
      container.querySelectorAll('.shop-by-room__product-slider').forEach(slider => {
        slider.classList.remove('active');
        slider.querySelectorAll('.shop-by-room__product-card').forEach(card => {
          card.classList.remove('active');
        });
      });
      
      const targetSlider = container.querySelector(`.shop-by-room__product-slider[data-room="${roomId}"][data-image="${imageIndex}"]`);
      if (targetSlider) {
        requestAnimationFrame(() => {
          targetSlider.classList.add('active');
          const firstProduct = targetSlider.querySelector('.shop-by-room__product-card[data-product-index="0"]');
          if (firstProduct) {
            firstProduct.classList.add('active');
          }
        });
      }
    });
  };

  window.showProduct = (roomId, imageIndex, productIndex, sectionId) => {
    const container = document.getElementById(`shop-by-room-${sectionId}`);
    if (!container) return;

    const slider = container.querySelector(
      `.shop-by-room__product-slider[data-room="${roomId}"][data-image="${imageIndex}"]`
    );
    if (!slider) return;

    requestAnimationFrame(() => {
      // Reset active cards
      slider.querySelectorAll('.shop-by-room__product-card').forEach(card => {
        card.classList.remove('active');
      });

      const targetCard = slider.querySelector(
        `.shop-by-room__product-card[data-product-index="${productIndex}"]`
      );

      if (!targetCard) return;

      targetCard.classList.add('active');

      /* -----------------------------
        VISIBILITY CHECK + SCROLL
      ------------------------------ */
      const rect = slider.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const isVisible =
        rect.top >= 0 &&
        rect.bottom <= viewportHeight;

      if (!isVisible) {
        slider.scrollIntoView({
          behavior: 'smooth',
          block: 'center' // keeps context, doesn’t hard snap
        });
      }
    });
  };


  const initShopByRoom = () => {
    const sections = document.querySelectorAll('.shop-by-room:not([data-initialized])');
    
    sections.forEach(section => {
      section.dataset.initialized = 'true';
      
      requestAnimationFrame(() => {
        section.querySelectorAll('.shop-by-room__product-card').forEach(card => {
          card.classList.remove('active');
        });
        
        section.querySelectorAll('.shop-by-room__product-slider').forEach(slider => {
          slider.classList.remove('active');
        });
        
        let firstActiveRoom = section.querySelector('.shop-by-room__room.active');
        let firstRoomId = null;
        
        if (firstActiveRoom) {
          firstRoomId = firstActiveRoom.dataset.roomId;
        } else {
          const firstRoom = section.querySelector('.shop-by-room__room');
          if (firstRoom) {
            firstRoom.classList.add('active');
            firstRoomId = firstRoom.dataset.roomId;
          }
        }
        
        if (firstRoomId) {
          const firstSlider = section.querySelector(`.shop-by-room__product-slider[data-room="${firstRoomId}"][data-image="0"]`);
          if (firstSlider) {
            firstSlider.classList.add('active');
            
            const firstProduct = firstSlider.querySelector('.shop-by-room__product-card[data-product-index="0"]');
            if (firstProduct) firstProduct.classList.add('active');
          }
        }
      });
    });
  };

  if (typeof Utils !== 'undefined' && Utils.initOnReady) {
    Utils.initOnReady(initShopByRoom);
  } else {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initShopByRoom, { once: true });
    } else {
      initShopByRoom();
    }
    document.addEventListener('shopify:section:load', initShopByRoom);
  }
})();

// ============================================
// REAL STORIES (Optimized with Intersection Observer)
// ============================================
(() => {
  const initDesktop = () => {
    document.querySelectorAll('[data-section-type="real-stories-desktop"]:not([data-initialized])').forEach(section => {
      section.dataset.initialized = 'true';

      const textEl = section.querySelector('#t');
      if (textEl && !textEl.dataset.textInitialized) {
        textEl.dataset.textInitialized = 'true';
        const txt = `${textEl.dataset.text} → ${textEl.dataset.text} → `;
        const chars = txt.split('');
        const rotation = 360 / chars.length;
        
        textEl.innerHTML = chars.map((c, i) => 
          `<span style="transform: rotate(${i * rotation}deg)">${c}</span>`
        ).join('');
      }

      const videos = section.querySelectorAll('.video-item');
      const contents = section.querySelectorAll('.content-item');
      const dots = section.querySelectorAll('.dot');
      const nextBtn = section.querySelector('#videoNext');
      
      if (videos.length === 0) return;

      let active = 0;

      const videoObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const video = entry.target.querySelector('video');
            if (!video) return;
            
            if (entry.isIntersecting && entry.target.classList.contains('video-pos-1')) {
              video.play().catch(() => {});
            } else {
              video.pause();
              video.currentTime = 0;
            }
          });
        },
        { threshold: 0.5 }
      );

      videos.forEach(v => videoObserver.observe(v));

      const updateActive = () => {
        requestAnimationFrame(() => {
          if (Utils.isMobile()) {
            videos.forEach((v, i) => v.classList.toggle('active-mobile', i === active));
          } else {
            videos.forEach((v, i) => {
              const index = (i - active + videos.length) % videos.length;
              v.className = v.className.replace(/video-(pos-\d|hidden)/g, '');
              
              if (index === 0) v.classList.add('video-pos-1');
              else if (index === 1) v.classList.add('video-pos-2');
              else if (index === 2) v.classList.add('video-pos-3');
              else v.classList.add('video-hidden');
              
              const video = v.querySelector('video');
              if (video) {
                if (index === 0) {
                  video.play().catch(() => {});
                } else {
                  video.pause();
                  video.currentTime = 0;
                }
              }
            });
          }

          contents.forEach((c, i) => c.style.display = i === active ? "block" : "none");
          dots.forEach((dot, i) => dot.classList.toggle('active', i === active));
        });
      };

      nextBtn?.addEventListener("click", () => {
        active = (active + 1) % videos.length;
        updateActive();
      });

      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          active = i;
          updateActive();
        });
      });

      const resizeHandler = () => updateActive();
      ResizeManager.subscribe(resizeHandler);

      CleanupManager.register(section.id || 'real-stories-desktop', () => {
        videoObserver.disconnect();
        ResizeManager.unsubscribe(resizeHandler);
      });

      updateActive();
    });
  };

  const initMobile = () => {
    const SVG_INACTIVE = `<svg class="icon" width="16" height="7" viewBox="0 0 16 7" fill="none"><path d="M15.4699 1.30063L12.9509 0.0807237C12.5844 -0.0952246 12.2993 0.0220755 12.0957 0.379837C11.2812 1.82848 9.93155 2.56159 7.99427 2.56159C6.34206 2.56159 4.79457 1.79329 3.86374 0.327052C3.54377 -0.0424389 3.17726 0.209753 3.17726 0.209753C3.17726 0.209753 0.425519 1.48245 0.140454 1.81088C-0.0806165 2.06307 0.0124652 2.31527 0.064824 2.42084C1.72285 4.87825 4.55023 6.30343 7.79647 6.30343C11.3161 6.30343 14.3704 4.90757 15.7666 2.16864C15.9703 1.78156 15.8772 1.48245 15.4699 1.3065V1.30063Z" fill="#3A3A3B"/></svg>`;
    
    const SVG_ACTIVE = `<svg class="icon active" width="19" height="18" viewBox="0 0 19 18" fill="none"><path d="M4.49122 9.90595H18.186C18.186 2.6862 14.102 6.10352e-05 9.27914 6.10352e-05C4.10144 6.10352e-05 0 3.58941 0 9.25494C0 9.47781 0.00581596 9.69481 0.0232689 9.91181H4.49122V9.90595ZM9.25587 3.46038C10.9488 3.46038 12.8977 4.1759 13.3282 7.0204H4.77629C5.46277 4.76826 7.23715 3.46038 9.25005 3.46038H9.25587Z" fill="#F15B25"/><path d="M17.0324 12.7752L14.5134 11.5553C14.1469 11.3794 13.8618 11.4967 13.6582 11.8544C12.8437 13.3031 11.494 14.0362 9.55677 14.0362C7.90456 14.0362 6.35707 13.2679 5.42624 11.8017C5.10627 11.4322 4.73976 11.6844 4.73976 11.6844C4.73976 11.6844 1.98802 12.9571 1.70295 13.2855C1.48188 13.5377 1.57497 13.7899 1.62732 13.8954C3.28535 16.3529 6.11273 17.778 9.35897 17.778C12.8786 17.778 15.9329 16.3822 17.3291 13.6433C17.5328 13.2562 17.4397 12.9571 17.0324 12.7811V12.7752Z" fill="#3A3A3B"/></svg>`;

    document.querySelectorAll('[data-mobile-slider]:not([data-initialized])').forEach(root => {
      root.dataset.initialized = 'true';

      const track = root.querySelector('.slider-track');
      const slides = Array.from(root.querySelectorAll('.slide'));
      if (!track || slides.length === 0) return;

      let dotsContainer = root.querySelector('.slider-dots');
      if (!dotsContainer) {
        dotsContainer = document.createElement('div');
        dotsContainer.className = 'slider-dots';
        root.appendChild(dotsContainer);
      }

      const gapPx = parseFloat(getComputedStyle(track).gap) || 16;
      let index = 0;
      let slideWidth = 0;
      let dragState = { startX: 0, currentX: 0, isDragging: false, startTime: 0 };

      const buildDots = () => {
        dotsContainer.innerHTML = slides.map((_, i) => 
          `<button type="button" class="slider-dot ${i === 0 ? 'active' : ''}" data-index="${i}" aria-label="Go to slide ${i + 1}">
            ${i === 0 ? SVG_ACTIVE : SVG_INACTIVE}
          </button>`
        ).join('');

        dotsContainer.querySelectorAll('.slider-dot').forEach(dot => {
          dot.addEventListener('click', () => {
            index = Number(dot.dataset.index);
            updateSlider();
          });
        });
      };

      const recomputeSizes = () => {
        const rect = slides[0].getBoundingClientRect();
        slideWidth = (rect.width || root.getBoundingClientRect().width) + gapPx;
      };

      const videoObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const video = entry.target.querySelector('.slide-video-wrap video') || entry.target.querySelector('video');
            if (!video) return;
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else {
              video.pause();
              video.currentTime = 0;
            }
          });
        },
        { threshold: 0.1, rootMargin: '50px' }
      );

      slides.forEach(s => videoObserver.observe(s));

      const firstSlide = slides[0];
      const firstVideo = firstSlide?.querySelector('.slide-video-wrap video') || firstSlide?.querySelector('video');
      if (firstVideo) {
        const section = root.closest('.real-stories-sec');
        const playFirstVideo = () => {
          firstVideo.play().catch(() => {});
        };
        if (section) {
          const sectionObs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) {
              playFirstVideo();
              sectionObs.disconnect();
            }
          }, { threshold: 0.01, rootMargin: '100px' });
          sectionObs.observe(section);
          if (section.getBoundingClientRect().top < window.innerHeight) {
            requestAnimationFrame(playFirstVideo);
          }
        } else {
          requestAnimationFrame(playFirstVideo);
        }
      }

      const updateSlider = (animate = true) => {
        recomputeSizes();
        index = Math.max(0, Math.min(slides.length - 1, index));
        
        requestAnimationFrame(() => {
          if (!animate) track.style.transition = 'none';
          track.style.transform = `translateX(-${index * slideWidth}px)`;
          if (!animate) {
            track.offsetHeight;
            track.style.transition = '';
          }

          dotsContainer.querySelectorAll('.slider-dot').forEach((dot, i) => {
            const isActive = i === index;
            dot.classList.toggle('active', isActive);
            dot.innerHTML = isActive ? SVG_ACTIVE : SVG_INACTIVE;
          });
        });
      };

      const handleStart = e => {
        dragState = {
          isDragging: true,
          startTime: Date.now(),
          startX: e.type.includes('mouse') ? e.pageX : e.touches[0].pageX,
          currentX: e.type.includes('mouse') ? e.pageX : e.touches[0].pageX
        };
        track.style.transition = 'none';
      };

      const handleMove = Utils.rafThrottle(e => {
        if (!dragState.isDragging) return;
        e.preventDefault();
        dragState.currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
        const diff = dragState.currentX - dragState.startX;
        track.style.transform = `translateX(-${index * slideWidth - diff}px)`;
      });

      const handleEnd = () => {
        if (!dragState.isDragging) return;
        dragState.isDragging = false;
        track.style.transition = '';
        
        const diff = dragState.currentX - dragState.startX;
        const velocity = Math.abs(diff) / (Date.now() - dragState.startTime);
        
        if (Math.abs(diff) > 50 || velocity > 0.5) {
          index = Math.max(0, Math.min(slides.length - 1, index + (diff > 0 ? -1 : 1)));
        }
        
        updateSlider();
      };

      ['touchstart', 'mousedown'].forEach(e => track.addEventListener(e, handleStart, { passive: true }));
      ['touchmove', 'mousemove'].forEach(e => track.addEventListener(e, handleMove, { passive: false }));
      ['touchend', 'mouseup', 'mouseleave', 'touchcancel'].forEach(e => track.addEventListener(e, handleEnd, { passive: true }));
      track.addEventListener('dragstart', e => e.preventDefault());

      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => requestAnimationFrame(() => updateSlider(false)));
        ro.observe(root);
        slides.forEach(s => ro.observe(s));
        
        CleanupManager.register(root.id || 'mobile-slider', () => {
          ro.disconnect();
          videoObserver.disconnect();
        });
      } else {
        const resizeHandler = () => updateSlider(false);
        ResizeManager.subscribe(resizeHandler);
        
        CleanupManager.register(root.id || 'mobile-slider', () => {
          ResizeManager.unsubscribe(resizeHandler);
          videoObserver.disconnect();
        });
      }

      root.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') index = Math.min(slides.length - 1, index + 1);
        if (e.key === 'ArrowLeft') index = Math.max(0, index - 1);
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') updateSlider();
      });

      buildDots();
      requestAnimationFrame(() => updateSlider(false));
    });
  };

  Utils.initOnReady(() => {
    initDesktop();
    initMobile();
  });
})();
// ============================================
// SEO: app widgets inject headings above/around the page H1 (MoEngage push opt-in title, Referrush
// popup titles). Swap them for <div>s with the same attributes; .sh-hN (base.css) keeps the look.
// ============================================
(() => {
  const SWAPS = [
    ['#moe-push-div h2#optInTitle', 'sh-h2'],
    ['#popup_content_referrush h3', 'sh-h3'],
  ];
  const toDiv = (h, cls) => {
    const d = h.ownerDocument.createElement('div');
    for (const a of h.attributes) d.setAttribute(a.name, a.value);
    d.classList.add(cls);
    d.innerHTML = h.innerHTML;
    h.replaceWith(d);
  };
  const run = () => SWAPS.forEach(([sel, cls]) => document.querySelectorAll(sel).forEach((h) => toDiv(h, cls)));
  // Referrush clones this template into <body>; clean it too so later clones have no headings.
  const tpl = document.getElementById('referrush-popup-template');
  if (tpl) tpl.content.querySelectorAll('h3').forEach((h) => toDiv(h, 'sh-h3'));
  run();
  const mo = new MutationObserver(run);
  mo.observe(document.body, { childList: true, subtree: true });
  // ponytail: stop watching after 2 min; raise it if the MoEngage soft-ask delay is set longer
  setTimeout(() => mo.disconnect(), 120000);
})();
